import type { AuthStrategy } from "../auth/types";

export interface MagnusFetchOptions {
  auth: AuthStrategy;
  baseUrl: string;
  /** Underlying fetch. Defaults to the global. */
  fetch?: typeof globalThis.fetch;
}

/**
 * Build a `fetch` that applies the auth strategy (headers + credentials)
 * to every request. The AI SDK's transport and the tRPC client both take
 * a custom `fetch`, so this is the single chokepoint where auth is applied.
 *
 * It also strips the `user-agent` header: the AI SDK sets a custom one the
 * browser may preserve, which trips up the backend.
 *
 * On a 401, the strategy is given one chance to drop its cached credential
 * (`reset()`) and the request is retried once. This is what lets the
 * `entraExchange` strategy recover when its cached Magnus session token has
 * expired server-side — without it, a long-lived host would 401 forever on
 * a still-valid Entra token. Streamed request bodies can't be replayed, so
 * they skip the retry.
 */
export function createMagnusFetch(
  options: MagnusFetchOptions
): typeof globalThis.fetch {
  const baseFetch = options.fetch ?? globalThis.fetch.bind(globalThis);

  async function applyAuth(
    init: RequestInit | undefined
  ): Promise<RequestInit> {
    const result = await options.auth.authenticate({
      baseUrl: options.baseUrl,
      fetch: baseFetch,
    });

    const headers = new Headers(init?.headers);
    headers.delete("user-agent");
    headers.delete("User-Agent");
    for (const [key, value] of Object.entries(result.headers ?? {})) {
      headers.set(key, value);
    }

    return {
      ...init,
      headers,
      // NOTE (external-SDK Phase 3): this defaults to "include", but the
      // cross-origin CORS layer added in Phase 1 (apps/web/lib/cors.ts)
      // deliberately omits `Access-Control-Allow-Credentials`. Browsers
      // reject credentialed cross-origin responses without that header, so a
      // truly cross-origin `baseUrl` consumer must run bearer-only with
      // `credentials: "omit"`. Harmless today (no cross-origin consumers yet).
      credentials: result.credentials ?? init?.credentials ?? "include",
    };
  }

  return async (input, init) => {
    const response = await baseFetch(input, await applyAuth(init));

    const canRetry =
      response.status === 401 &&
      typeof options.auth.reset === "function" &&
      !(init?.body instanceof ReadableStream);
    if (canRetry) {
      // Force the strategy to re-resolve its credential on the next call
      // (e.g. a fresh token exchange), then retry exactly once.
      options.auth.reset?.();
      return baseFetch(input, await applyAuth(init));
    }

    return response;
  };
}
